import { ApiClient, ApiResponse } from '../client';
import { Product } from '../models';

/**
 * Opciones de búsqueda
 */
export interface SearchOptions {
  query: string;
  filters?: string;
  sort?: string;
  page?: number;
  limit?: number;
  stockLessThan?: number;
}

/**
 * Metadatos adicionales de búsqueda (ya vienen en ApiResponse)
 */
export interface SearchMetadata {
  total?: number;
  processingTimeMs?: number;
  page?: number;
  limit?: number;
  hybridSearch?: boolean;
  semanticRatio?: number;
}

/**
 * Servicio para búsqueda de productos
 */
export class SearchService {
  private client: ApiClient;

  constructor(client: ApiClient) {
    this.client = client;
  }

  /**
   * Buscar productos
   * @param options Opciones de búsqueda
   * @returns ApiResponse con array de productos en data y metadatos en propiedades adicionales
   */
  public async searchProducts(options: SearchOptions): Promise<ApiResponse<Product[]>> {
    const params: Record<string, string | number> = {
      q: options.query,
    };

    if (options.filters) {
      params.filters = options.filters;
    }

    if (options.sort) {
      params.sort = options.sort;
    }

    if (options.page) {
      params.page = options.page;
    }

    if (options.limit) {
      params.limit = options.limit;
    }

    if (options.stockLessThan !== undefined) {
      params.stockLessThan = options.stockLessThan;
    }

    // Backend devuelve: {success: true, data: [productos], count, total, hybridSearch, etc}
    return await this.client.get<Product[]>('/products/search', { params });
  }

  /**
   * Búsqueda con autocompletado (sugerencias)
   * @param query Texto de búsqueda
   * @param limit Límite de resultados (por defecto 5)
   */
  public async autocomplete(query: string, limit: number = 5): Promise<ApiResponse<Product[]>> {
    if (!query || query.trim().length === 0) {
      return {
        success: true,
        data: []
      };
    }

    // searchProducts ya devuelve ApiResponse<Product[]> directamente
    return await this.searchProducts({
      query,
      limit,
      page: 1
    });
  }

  /**
   * Buscar por categoría
   * @param categoryId ID de la categoría
   * @param limit Límite de resultados
   */
  public async searchByCategory(categoryId: string, limit: number = 20): Promise<ApiResponse<Product[]>> {
    return await this.searchProducts({
      query: '',
      filters: `category = '${categoryId}'`,
      limit
    });
  }

  /**
   * Buscar por marca
   * @param brand Nombre de la marca
   * @param limit Límite de resultados
   */
  public async searchByBrand(brand: string, limit: number = 20): Promise<ApiResponse<Product[]>> {
    return await this.searchProducts({
      query: '',
      filters: `brand = '${brand}'`,
      limit
    });
  }

  /**
   * Búsqueda con múltiples filtros
   * @param query Texto de búsqueda
   * @param filters Objeto con filtros
   * @param sort Ordenamiento
   * @param page Página
   * @param limit Límite de resultados
   */
  public async advancedSearch(
    query: string,
    filters: {
      category?: string | string[];
      brand?: string;
      minPrice?: number;
      maxPrice?: number;
      tags?: string[];
      isActive?: boolean | 'all';
      stockLessThan?: number;
    } = {},
    sort?: string,
    page: number = 1,
    limit: number = 20
  ): Promise<ApiResponse<Product[]>> {
    const filterParts: string[] = [];

    if (filters.category) {
      // Usar categoryPath para que el backend pueda expandir a subcategorías
      if (Array.isArray(filters.category) && filters.category.length > 0) {
        const categoryFilters = filters.category.map(c => `categoryPath = "${c}"`).join(' OR ');
        filterParts.push(`(${categoryFilters})`);
      } else if (typeof filters.category === 'string') {
        filterParts.push(`categoryPath = "${filters.category}"`);
      }
    }

    if (filters.brand) {
      // Filtrar por marcas (puede ser múltiples separadas por coma)
      const brandList = filters.brand.split(',').map(b => b.trim());
      if (brandList.length > 1) {
        const brandFilters = brandList.map(b => `brand = "${b}"`).join(' OR ');
        filterParts.push(`(${brandFilters})`);
      } else {
        filterParts.push(`brand = "${filters.brand}"`);
      }
    }

    if (filters.minPrice !== undefined) {
      filterParts.push(`price >= ${filters.minPrice}`);
    }

    if (filters.maxPrice !== undefined) {
      filterParts.push(`price <= ${filters.maxPrice}`);
    }

    if (filters.tags && filters.tags.length > 0) {
      const tagsFilter = filters.tags.map(tag => `tags = "${tag}"`).join(' OR ');
      filterParts.push(`(${tagsFilter})`);
    }

    if (filters.isActive !== undefined && filters.isActive !== 'all') {
      filterParts.push(`isActive = ${filters.isActive}`);
    }

    const filterString = filterParts.length > 0 ? filterParts.join(' AND ') : undefined;

    return await this.searchProducts({
      query: query || '',
      filters: filterString,
      sort,
      page,
      limit,
      stockLessThan: filters.stockLessThan
    });
  }
}
